// Error logging utility
// This can be easily extended to integrate with services like Sentry, LogRocket, etc.

interface ErrorLogData {
  error: Error;
  errorInfo?: React.ErrorInfo;
  userId?: string;
  context?: Record<string, unknown>;
  timestamp: Date;
  userAgent: string;
  url: string;
}

interface StoredError {
  message: string;
  stack?: string;
  timestamp: Date;
  url: string;
}

class ErrorLogger {
  private static instance: ErrorLogger;

  private constructor() {}

  static getInstance(): ErrorLogger {
    if (!ErrorLogger.instance) {
      ErrorLogger.instance = new ErrorLogger();
    }
    return ErrorLogger.instance;
  }

  logError(error: Error, errorInfo?: React.ErrorInfo, context?: Record<string, unknown>) {
    const errorData: ErrorLogData = {
      error,
      errorInfo,
      context,
      timestamp: new Date(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    };

    // Log to console in development
    if (import.meta.env.DEV) {
      console.group('🚨 Error Logged');
      console.error('Error:', error);
      if (errorInfo) {
        console.error('Component Stack:', errorInfo.componentStack);
      }
      if (context) {
        console.error('Context:', context);
      }
      console.groupEnd();
    }

    // In production, send to error monitoring service
    if (import.meta.env.PROD) {
      this.sendToMonitoringService(errorData);
    }

    // Store in local storage for debugging (optional)
    this.storeLocalError(errorData);
  }

  private sendToMonitoringService(errorData: ErrorLogData) {
    // TODO: Integrate with your preferred error monitoring service
    // Examples:
    
    // Sentry
    // Sentry.captureException(errorData.error, {
    //   contexts: { errorInfo: errorData.errorInfo, custom: errorData.context }
    // });
    
    // Custom API endpoint
    // fetch('/api/errors', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify({
    //     message: errorData.error.message,
    //     stack: errorData.error.stack,
    //     componentStack: errorData.errorInfo?.componentStack,
    //     context: errorData.context,
    //     timestamp: errorData.timestamp,
    //     userAgent: errorData.userAgent,
    //     url: errorData.url
    //   })
    // });

    console.log('Error would be sent to monitoring service:', errorData);
  }

  private storeLocalError(errorData: ErrorLogData) {
    try {
      const stored = localStorage.getItem('app_errors') || '[]';
      const errors = JSON.parse(stored);
      
      // Keep only last 10 errors
      errors.push({
        message: errorData.error.message,
        stack: errorData.error.stack,
        timestamp: errorData.timestamp,
        url: errorData.url
      });
      
      if (errors.length > 10) {
        errors.shift();
      }
      
      localStorage.setItem('app_errors', JSON.stringify(errors));
    } catch (e) {
      console.warn('Could not store error in localStorage:', e);
    }
  }

  // Method to retrieve stored errors (useful for debugging)
  getStoredErrors(): StoredError[] {
    try {
      const stored = localStorage.getItem('app_errors') || '[]';
      return JSON.parse(stored);
    } catch (e) {
      console.warn('Could not retrieve stored errors:', e);
      return [];
    }
  }

  // Clear stored errors
  clearStoredErrors(): void {
    localStorage.removeItem('app_errors');
  }
}

// Export singleton instance
export const errorLogger = ErrorLogger.getInstance();

// Global error handler for unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  errorLogger.logError(
    new Error(`Unhandled Promise Rejection: ${event.reason}`),
    undefined,
    { type: 'unhandledrejection', reason: event.reason }
  );
});

// Global error handler for uncaught exceptions
window.addEventListener('error', (event) => {
  errorLogger.logError(
    new Error(event.message),
    undefined,
    { 
      type: 'uncaughtexception',
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno
    }
  );
});

export default ErrorLogger;
